import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../../../components/buttons/button";
import { TextInput } from "../../../../components/inputs/textInput";
import { InfoComponent } from "../../../../components/notification/infoComponent";
import { AuthHeader } from "../../../../components/typography/authHeader";
import {
  useAdminLoginMutation,
  useInitiateTwoFaMutation,
} from "../../../../redux/features/admin/auth/auth.api";
import { BaseApiErrorResponse } from "../../../../utils/types";
import { SignInSchema } from "../../../../utils/validations";
import { authSliceActions } from "../../../../redux/features/auth/auth.slice";
import { useAppDispatch } from "../../../../redux/hooks";

export const AdminLoginPage = () => {
  const [login, { isLoading, isError, error }] = useAdminLoginMutation();
  const [
    initiateTwoFa,
    {
      isLoading: initiateLoading,
      isError: initiateIsError,
      error: initiateError,
    },
  ] = useInitiateTwoFaMutation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { values, handleBlur, handleChange, handleSubmit, errors, touched } =
    useFormik({
      initialValues: {
        email: "",
        password: "",
      },
      validationSchema: SignInSchema,
      onSubmit: async (vals) => {
        const response = await login({
          email: vals.email,
          password: vals.password,
        });

        if (response.data?.status === "success") {
          const profile = response.data.data;

          if (profile.is2faSetupComplete) {
            dispatch(authSliceActions.updateAdminProfile({ profile }));
            navigate("/verify-2fa/" + profile._id);
          } else {
            const setupResponse = await initiateTwoFa({ email: profile.email });
            if (setupResponse.data?.status === "success") {
              dispatch(
                authSliceActions.updateAdminProfile({
                  profile: setupResponse.data.data,
                }),
              );
              navigate("/initiate-2fa/" + setupResponse.data.data._id);
            }
          }
        }
      },
    });

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      <AuthHeader
        header="Admin Login"
        text={<span>Welcome to Tikket Backoffice.</span>}
      />

      <div className="mt-10 flex flex-col gap-5">
        {(isError || initiateIsError) && (
          <InfoComponent
            title="Error"
            text={
              (error as unknown as BaseApiErrorResponse)?.data?.message ??
              (initiateError as unknown as BaseApiErrorResponse)?.data
                ?.message ??
              "An error occurred!"
            }
            type="error"
          />
        )}

        <TextInput
          type="text"
          name="email"
          value={values.email}
          placeholder="e.g. example@email.com"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Email"
          errors={errors.email && touched.email ? errors.email : ""}
          autoFocus
          autoFocusOnMobile={false}
        />

        <TextInput
          type="password"
          name="password"
          value={values.password}
          placeholder="Enter your password"
          onChange={handleChange}
          onBlur={handleBlur}
          hasTitle
          title="Password"
          errors={errors.password && touched.password ? errors.password : ""}
        />

        <Link
          to={`/forgot-password${!errors.email ? `?email=${encodeURIComponent(values.email)}` : ""}`}
          className="text-sm font-bold text-grey-700"
        >
          Forgot Password?
        </Link>

        <Button
          type="submit"
          text="Log in"
          variation="primary"
          className="mt-5"
          loading={isLoading || initiateLoading}
          disabled={isLoading || initiateLoading}
        />
      </div>
    </form>
  );
};
